import React, { useEffect, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import NumberInput from '@/components/ui/NumberInput';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { TipoMoneda } from '@/types/costo';
import type {
  CotizacionDolar,
  PresupuestoProforma,
  PresupuestoProformaDatosFormData,
} from '../types';
import {
  faltaCotizacionParaIgualar,
  igualarAlActivar,
  igualarMontos,
  type OrigenIgualdad,
} from '../utils/baseImponible';
import { ConsultarCotizacionDialog } from './ConsultarCotizacionDialog';

interface DatosPartesFormProps {
  presupuesto: PresupuestoProforma;
  onGuardar: (data: PresupuestoProformaDatosFormData) => Promise<unknown>;
  readOnly?: boolean;
  isGuardando?: boolean;
}

type CampoTexto = keyof Omit<
  PresupuestoProformaDatosFormData,
  | 'nombre'
  | 'monto_venta'
  | 'monto_venta_moneda'
  | 'base_imponible'
  | 'base_imponible_moneda'
  | 'base_imponible_igual_monto_venta'
  | 'avaluo_fiscal'
  | 'cotizacion_dolar'
  | 'cotizacion_dolar_origen'
  | 'cotizacion_dolar_tipo'
  | 'cotizacion_dolar_fecha'
>;

const CAMPOS_ADQUIRIENTE: Array<{ campo: CampoTexto; label: string }> = [
  { campo: 'adquiriente_nombre', label: 'Nombre o razón social' },
  { campo: 'adquiriente_cuit', label: 'CUIT / DNI' },
  { campo: 'adquiriente_domicilio', label: 'Domicilio' },
  { campo: 'adquiriente_condicion_iva', label: 'Condición frente al IVA' },
];

const CAMPOS_TRANSFERENTE: Array<{ campo: CampoTexto; label: string }> = [
  { campo: 'transferente_nombre', label: 'Nombre o razón social' },
  { campo: 'transferente_cuit', label: 'CUIT / DNI' },
  { campo: 'transferente_domicilio', label: 'Domicilio' },
  { campo: 'transferente_condicion_iva', label: 'Condición frente al IVA' },
];

/**
 * La base imponible siempre está en pesos: no se ofrece elegir la moneda, pero
 * el campo se mantiene en el modelo por si vuelve a hacer falta.
 */
const MONEDA_BASE_IMPONIBLE: TipoMoneda = 'ARS';

const desdePresupuesto = (
  presupuesto: PresupuestoProforma
): PresupuestoProformaDatosFormData => ({
  adquiriente_nombre: presupuesto.adquiriente.nombre ?? '',
  adquiriente_cuit: presupuesto.adquiriente.cuit ?? '',
  adquiriente_domicilio: presupuesto.adquiriente.domicilio ?? '',
  adquiriente_condicion_iva: presupuesto.adquiriente.condicionIva ?? '',
  transferente_nombre: presupuesto.transferente.nombre ?? '',
  transferente_cuit: presupuesto.transferente.cuit ?? '',
  transferente_domicilio: presupuesto.transferente.domicilio ?? '',
  transferente_condicion_iva: presupuesto.transferente.condicionIva ?? '',
  inmueble_ubicacion: presupuesto.inmuebleUbicacion ?? '',
  inmueble_padron: presupuesto.inmueblePadron ?? '',
  condicion_venta: presupuesto.condicionVenta ?? '',
  monto_venta: presupuesto.montoVenta ?? null,
  monto_venta_moneda: presupuesto.montoVentaMoneda ?? 'ARS',
  base_imponible: presupuesto.baseImponible ?? null,
  base_imponible_moneda: MONEDA_BASE_IMPONIBLE,
  base_imponible_igual_monto_venta: presupuesto.baseImponibleIgualMontoVenta ?? false,
  avaluo_fiscal: presupuesto.avaluoFiscal ?? null,
  cotizacion_dolar: presupuesto.cotizacionDolar ?? null,
});

/**
 * Con la base imponible igual al monto de venta, el importe del que sale el
 * otro según el campo editado. Si cambia la cotización, manda el monto de venta.
 */
const ORIGEN_IGUALDAD: Partial<Record<keyof PresupuestoProformaDatosFormData, OrigenIgualdad>> = {
  monto_venta: 'monto_venta',
  monto_venta_moneda: 'monto_venta',
  cotizacion_dolar: 'monto_venta',
  base_imponible: 'base_imponible',
};

/**
 * Asigna un campo y, con la base imponible igual al monto de venta, recalcula
 * el otro importe.
 */
const conCampo = (
  valores: PresupuestoProformaDatosFormData,
  campo: keyof PresupuestoProformaDatosFormData,
  valor: unknown
): PresupuestoProformaDatosFormData => {
  const nuevos = { ...valores, [campo]: valor };

  if (!nuevos.base_imponible_igual_monto_venta) return nuevos;
  if (campo === 'base_imponible_igual_monto_venta') return igualarAlActivar(nuevos);

  const origen = ORIGEN_IGUALDAD[campo];
  return origen ? igualarMontos(nuevos, origen) : nuevos;
};

/**
 * Datos opcionales del inmueble, la operación y las partes. Sirven para
 * completar la exportación; no son obligatorios para guardar el presupuesto ni
 * bloquean la exportación si faltan (los vacíos no se imprimen).
 */
export const DatosPartesForm: React.FC<DatosPartesFormProps> = ({
  presupuesto,
  onGuardar,
  readOnly = false,
  isGuardando = false,
}) => {
  const [abierto, setAbierto] = useState(false);
  const [consultandoCotizacion, setConsultandoCotizacion] = useState(false);
  const [valores, setValores] = useState<PresupuestoProformaDatosFormData>(() =>
    desdePresupuesto(presupuesto)
  );

  useEffect(() => {
    setValores(desdePresupuesto(presupuesto));
  }, [presupuesto]);

  const setCampo = (campo: keyof PresupuestoProformaDatosFormData, valor: unknown) =>
    setValores((actual) => conCampo(actual, campo, valor));

  // Elegir un valor de la API lo carga y lo guarda enseguida, con su origen
  // para el historial.
  const usarCotizacion = async (valor: number, cotizacion: CotizacionDolar) => {
    const datos = conCampo(valores, 'cotizacion_dolar', valor);
    setValores(datos);
    await onGuardar({
      ...datos,
      cotizacion_dolar_origen: 'api',
      cotizacion_dolar_tipo: cotizacion.tipo,
      cotizacion_dolar_fecha: cotizacion.fechaActualizacion,
    });
    setConsultandoCotizacion(false);
  };

  const renderMoneda = (campo: 'monto_venta_moneda', label: string) => (
    <Select
      value={valores[campo] ?? 'ARS'}
      onValueChange={(moneda: TipoMoneda) => setCampo(campo, moneda)}
      disabled={readOnly || isGuardando}
    >
      <SelectTrigger className="w-24 shrink-0" aria-label={`Moneda de ${label}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="ARS">ARS</SelectItem>
        <SelectItem value="USD">USD</SelectItem>
      </SelectContent>
    </Select>
  );

  const renderCampos = (campos: Array<{ campo: CampoTexto; label: string }>) =>
    campos.map(({ campo, label }) => (
      <div key={campo} className="space-y-1.5">
        <Label htmlFor={campo}>{label}</Label>
        <Input
          id={campo}
          value={(valores[campo] as string) ?? ''}
          onChange={(event) => setCampo(campo, event.target.value)}
          disabled={readOnly || isGuardando}
          placeholder="No informado"
        />
      </div>
    ));

  return (
    <Card>
      <CardHeader className="py-3">
        <button
          type="button"
          onClick={() => setAbierto((actual) => !actual)}
          className="flex w-full items-center gap-2 text-left"
          aria-expanded={abierto}
        >
          {abierto ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
          <CardTitle className="text-base">
            Datos de las partes, el inmueble y la operación
          </CardTitle>
          <span className="ml-auto text-xs text-muted-foreground">Opcionales</span>
        </button>
      </CardHeader>

      {abierto && (
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-muted-foreground">Inmueble y operación</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="inmueble_ubicacion">Ubicación o descripción del inmueble</Label>
                <Input
                  id="inmueble_ubicacion"
                  value={valores.inmueble_ubicacion ?? ''}
                  onChange={(event) => setCampo('inmueble_ubicacion', event.target.value)}
                  disabled={readOnly || isGuardando}
                  placeholder="No informado"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="inmueble_padron">N° de padrón</Label>
                <Input
                  id="inmueble_padron"
                  value={valores.inmueble_padron ?? ''}
                  onChange={(event) => setCampo('inmueble_padron', event.target.value)}
                  disabled={readOnly || isGuardando}
                  maxLength={50}
                  placeholder="No informado"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="condicion_venta">Condición de venta</Label>
                <Input
                  id="condicion_venta"
                  value={valores.condicion_venta ?? ''}
                  onChange={(event) => setCampo('condicion_venta', event.target.value)}
                  disabled={readOnly || isGuardando}
                  placeholder="No informado"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="monto_venta">Monto de venta</Label>
                <div className="flex gap-2">
                  <NumberInput
                    id="monto_venta"
                    currency
                    decimals={2}
                    min={0}
                    value={valores.monto_venta ?? ''}
                    onAccept={(valor) => setCampo('monto_venta', valor)}
                    disabled={readOnly || isGuardando}
                    placeholder="0,00"
                    containerClassName="flex-1"
                  />
                  {renderMoneda('monto_venta_moneda', 'monto de venta')}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="base_imponible">Base imponible</Label>
                <div className="flex gap-2">
                  <NumberInput
                    id="base_imponible"
                    currency
                    decimals={2}
                    min={0}
                    value={valores.base_imponible ?? ''}
                    onAccept={(valor) => setCampo('base_imponible', valor)}
                    disabled={readOnly || isGuardando}
                    placeholder="0,00"
                    containerClassName="flex-1"
                  />
                  <span className="flex w-24 shrink-0 items-center justify-center rounded-md border bg-muted text-sm text-muted-foreground">
                    {MONEDA_BASE_IMPONIBLE}
                  </span>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <Checkbox
                    id="base_imponible_igual_monto_venta"
                    checked={valores.base_imponible_igual_monto_venta ?? false}
                    onCheckedChange={(checked) =>
                      setCampo('base_imponible_igual_monto_venta', checked === true)
                    }
                    disabled={readOnly || isGuardando}
                  />
                  <Label htmlFor="base_imponible_igual_monto_venta" className="font-normal">
                    Igual al monto de venta
                  </Label>
                </div>
                {faltaCotizacionParaIgualar(valores) && (
                  <p className="inline-flex items-center gap-1 text-xs text-amber-700">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Cargá la cotización del dólar para igualar montos en distintas monedas
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cotizacion_dolar">Cotización del dólar (ARS por USD)</Label>
                <div className="flex gap-2">
                  <NumberInput
                    id="cotizacion_dolar"
                    currency
                    decimals={2}
                    min={0}
                    value={valores.cotizacion_dolar ?? ''}
                    onAccept={(valor) => setCampo('cotizacion_dolar', valor)}
                    disabled={readOnly || isGuardando}
                    placeholder="No informada"
                    containerClassName="flex-1"
                  />
                  {!readOnly && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setConsultandoCotizacion(true)}
                      disabled={isGuardando}
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Consultar cotización
                    </Button>
                  )}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="avaluo_fiscal">Avalúo fiscal</Label>
                <NumberInput
                  id="avaluo_fiscal"
                  currency
                  decimals={2}
                  min={0}
                  value={valores.avaluo_fiscal ?? ''}
                  onAccept={(valor) => setCampo('avaluo_fiscal', valor)}
                  disabled={readOnly || isGuardando}
                  placeholder="0,00"
                />
              </div>
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground">Adquiriente</h3>
              {renderCampos(CAMPOS_ADQUIRIENTE)}
            </div>
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground">Transferente</h3>
              {renderCampos(CAMPOS_TRANSFERENTE)}
            </div>
          </div>

          {!readOnly && (
            <div className="flex justify-end">
              <Button onClick={() => void onGuardar(valores)} disabled={isGuardando}>
                Guardar datos
              </Button>
            </div>
          )}
        </CardContent>
      )}

      {!readOnly && consultandoCotizacion && (
        <ConsultarCotizacionDialog
          open={consultandoCotizacion}
          onOpenChange={setConsultandoCotizacion}
          onConfirm={usarCotizacion}
          isGuardando={isGuardando}
        />
      )}
    </Card>
  );
};
